import { featureFlags } from "$global-providers/feature-flags-provider/feature-flags";
import { useFeatureFlag } from "$global-providers/feature-flags-provider/provider";
import { useRxCollectionConsultation } from "$global-providers/rxdb-provider/collections/consultation";
import { useRxCollectionDisease } from "$global-providers/rxdb-provider/collections/disease";
import {
  type Patient,
  useRxCollectionPatient,
} from "$global-providers/rxdb-provider/collections/patient";
import { isAfter, isBefore, min, subWeeks } from "date-fns";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { calculatePatientVaccinationSchedule } from "./schedule-calculator";
import {
  allVaccineCodesToDetailsMap,
  type VaccinationSchedule,
  type VaccineCode,
  vaccinationScheduleTemplate,
} from "./vaccination-schedule";

export const EARLIEST_JS_DATE = new Date(-8640000000000000).toISOString();

import { isChildPatient } from "../is-child-patient";
import type { VaccinationScheduleDose } from "./vaccination-schedule";

const PENTA_COMPONENT_VACCINES = [
  "$IMM11", // Penta component vaccines
  "$IMM4",
  "$IMM38",
  "$IMM3",
] as const;

export enum VaccinationNudgeType {
  MISSING_VACCINES = "MISSING_VACCINES",
  UNUSUAL_HISTORY = "UNUSUAL_HISTORY",
  EARLY_VACCINES = "EARLY_VACCINES",
  DUPLICATE_VACCINES = "DUPLICATE_VACCINES",
}

type VaccinationNudge = {
  type: VaccinationNudgeType;
  vaccines: Array<{ displayName: string; code: VaccineCode }>;
};

interface VaccinationsService {
  patient: Patient | null;
  getIsChildPatient: () => boolean;
  getPatientVaccinationSchedule: () => VaccinationSchedule;
  getPatientRecommendedVaccines: () => { code: string }[];

  getVaccinationNudge: (
    formVaccinations: Array<{ code: string; givenOn?: Date }>,
  ) => VaccinationNudge | null;
  getUnusualVaccineNames: () => { displayName: string; code: VaccineCode }[];
}

const isDue = (dose: VaccinationScheduleDose): boolean => {
  if (!dose.dueDate) return false;
  const nowDate = new Date();
  if (isBefore(nowDate, dose.dueDate)) return false;
  if (dose.missingDate && isAfter(nowDate, dose.missingDate)) return false;
  return true;
};

export type VaccinationsReceivedMap = { [code: string]: Date[] };

const useCreateVaccinationsService = (
  patientId: string,
): VaccinationsService => {
  const [scheduleFlag] = useFeatureFlag(featureFlags.immunisations.schedule);
  const [nudgesFlag] = useFeatureFlag(featureFlags.immunisations.nudges);

  const rxCollectionConsultations = useRxCollectionConsultation();
  const rxCollectionDiseases = useRxCollectionDisease();
  const rxCollectionPatients = useRxCollectionPatient();

  const [patientVaccinationsReceived, setPatientVaccinationsReceived] =
    useState<VaccinationsReceivedMap>({});

  const [unusualVaccines, setUnusualVaccines] = useState<string[]>([]);

  const [patient, setPatient] = useState<Patient | null>(null);
  const [diseaseIdToCodeMap, setDiseaseIdToCodeMap] = useState<{
    [id: string]: string;
  } | null>(null);

  const setNewUnusualVaccines = useCallback((completingCode: VaccineCode) => {
    setUnusualVaccines((prev) =>
      Array.from(new Set([...prev, completingCode])),
    );
  }, []);

  useEffect(() => {
    if (!rxCollectionDiseases) {
      return;
    }
    (async () => {
      const diseases = await rxCollectionDiseases
        .find({
          selector: {
            $or: [
              { "catalogueCoding.icd11.code": { $regex: "^\\$IMM" } },
              { "catalogueCoding.icd10.code": { $regex: "^\\$IMM" } },
            ],
          },
        })
        .exec();
      const map = Object.fromEntries(
        diseases.map(({ id, catalogueCoding }) => [
          id,
          catalogueCoding?.icd11?.code || catalogueCoding?.icd10?.code || "",
        ]),
      );
      setDiseaseIdToCodeMap(map);
    })();
  }, [rxCollectionDiseases]);

  useEffect(() => {
    if (!rxCollectionConsultations || !diseaseIdToCodeMap) return;

    const subscription = rxCollectionConsultations
      .find({ selector: { patientId } })
      .$.subscribe((consultations) => {
        if (!consultations) return;

        const unusualCodes = new Set<VaccineCode>();
        const vaccinationsByDate: VaccinationsReceivedMap =
          consultations.reduce(
            (
              acc: VaccinationsReceivedMap,
              { diseaseIds, backdatedTo, meta: { createdAt } },
            ) => {
              for (const diseaseId of diseaseIds) {
                const code = diseaseIdToCodeMap[diseaseId];
                if (!code) continue;

                const newDate = new Date(backdatedTo ?? createdAt);

                if (!acc[code]) {
                  acc[code] = [];
                } else {
                  if (
                    !(PENTA_COMPONENT_VACCINES as readonly string[]).includes(
                      code,
                    )
                  ) {
                    unusualCodes.add(code);
                  }
                }

                acc[code] = Array.from(new Set([...acc[code], newDate])).sort(
                  (a, b) => a.getTime() - b.getTime(),
                );
              }
              return acc;
            },
            {} as VaccinationsReceivedMap,
          );

        setPatientVaccinationsReceived(vaccinationsByDate);
        setUnusualVaccines((prev) =>
          Array.from(new Set([...prev, ...unusualCodes])),
        );
      });

    return () => subscription.unsubscribe();
  }, [patientId, rxCollectionConsultations, diseaseIdToCodeMap]);

  useEffect(() => {
    if (!rxCollectionPatients) return;
    const rxPatientQuery = rxCollectionPatients.findOne({
      selector: { id: patientId },
    });

    const subscription = rxPatientQuery.$.subscribe((result) => {
      if (!result || !result._data) {
        return;
      }
      setPatient(result._data);
    });

    return () => subscription.unsubscribe();
  }, [patientId, rxCollectionPatients]);

  const patientVaccinationsSchedule = useMemo(
    () =>
      calculatePatientVaccinationSchedule({
        patient,
        vaccinations: patientVaccinationsReceived,
        setUnusualVaccines: setNewUnusualVaccines,
      }),
    [patientVaccinationsReceived, patient, setNewUnusualVaccines],
  );

  const getPatientRecommendedVaccines = useCallback(() => {
    if (!scheduleFlag) return [];
    const VACCINE_CODES_TO_EXCLUDE_FROM_RECOMMENDATIONS = new Set([
      "$IMM6", // Penta duplicates
      "$IMM20",
      "$IMM21",
      ...PENTA_COMPONENT_VACCINES,
    ]);

    const codeMap: { [code: string]: Date } = Object.values(
      patientVaccinationsSchedule,
    ).reduce((map, vaccineScheduleDisease) => {
      for (const dose of vaccineScheduleDisease.doses) {
        if (isDue(dose)) {
          for (const code of dose.codes) {
            if (!VACCINE_CODES_TO_EXCLUDE_FROM_RECOMMENDATIONS.has(code)) {
              map[code] = map[code]
                ? min([dose.dueDate, map[code]])
                : dose.dueDate;
            }
          }
        }
      }

      return map;
    }, {});

    return Object.entries(codeMap)
      .map(([code, dueDate]) => ({ code, dueDate }))
      .sort((a, b) => a.dueDate.getTime() - b.dueDate.getTime());
  }, [patientVaccinationsSchedule, scheduleFlag]);

  const getUnusualVaccineNames = useCallback(() => {
    return unusualVaccines.map((code) => ({
      displayName: allVaccineCodesToDetailsMap[code].displayName,
      code,
    }));
  }, [unusualVaccines]);

  return {
    patient,
    getIsChildPatient: () => isChildPatient(new Date(patient?.dob ?? "")),
    getPatientVaccinationSchedule: () => patientVaccinationsSchedule,
    getPatientRecommendedVaccines,
    getVaccinationNudge: (formVaccinations) =>
      nudgesFlag
        ? getVaccinationNudge(
            formVaccinations,
            patientVaccinationsSchedule,
            scheduleFlag,
          )
        : null,
    getUnusualVaccineNames,
  };
};

const VaccinationsServiceContext = createContext<VaccinationsService | null>(
  null,
);

const FlagOnProvider: FCwC<{
  patientId?: string;
}> = ({ patientId, children }) => {
  const service = useCreateVaccinationsService(patientId);

  return (
    <VaccinationsServiceContext.Provider value={service}>
      {children}
    </VaccinationsServiceContext.Provider>
  );
};

export const VaccinationsProvider: FCwC<{ patientId?: string }> = ({
  patientId,
  children,
}) => {
  const [addVaccineFlowFlag] = useFeatureFlag(
    featureFlags.immunisations.addVaccineFlow,
  );

  if (!addVaccineFlowFlag) {
    return (
      <VaccinationsServiceContext.Provider
        value={{
          patient: null,
          getIsChildPatient: () => false,
          getPatientVaccinationSchedule: () => vaccinationScheduleTemplate,
          getPatientRecommendedVaccines: () => [],
          getVaccinationNudge: () => null,
          getUnusualVaccineNames: () => [],
        }}
      >
        {children}
      </VaccinationsServiceContext.Provider>
    );
  }

  return <FlagOnProvider patientId={patientId}>{children}</FlagOnProvider>;
};

export const useVaccinationsService = () => {
  const context = useContext(VaccinationsServiceContext);
  if (!context) {
    throw new Error(
      "useVaccinationsService must be used within an VaccinationsServiceProvider.",
    );
  }
  return context;
};

const getVaccinationNudge = (
  formVaccinations: Array<{ code: string; givenOn?: Date }>,
  vaccinationSchedule: VaccinationSchedule,
  isScheduleEnabled: boolean,
): VaccinationNudge | null => {
  const { missing, early, duplicate } = getMissingEarlyAndDuplicateVaccines(
    formVaccinations,
    vaccinationSchedule,
  );
  if (missing.length > 0) {
    return {
      type: VaccinationNudgeType.MISSING_VACCINES,
      vaccines: missing.map((code) => ({
        code,
        displayName: allVaccineCodesToDetailsMap[code].displayName,
      })),
    };
  }

  if (early.length > 0 && isScheduleEnabled) {
    return {
      type: VaccinationNudgeType.EARLY_VACCINES,
      vaccines: early.map((code) => ({
        code,
        displayName: allVaccineCodesToDetailsMap[code].displayName,
      })),
    };
  }

  if (duplicate.length > 0) {
    return {
      type: VaccinationNudgeType.DUPLICATE_VACCINES,
      vaccines: duplicate.map((code) => ({
        code,
        displayName: allVaccineCodesToDetailsMap[code].displayName,
      })),
    };
  }

  return null;
};

export const getMissingEarlyAndDuplicateVaccines = (
  formVaccinations: Array<{ code: string; givenOn?: Date }>,
  vaccinationSchedule: VaccinationSchedule,
) => {
  const missingVaccines = new Set<VaccineCode>();
  const earlyVaccinations = new Set<VaccineCode>();
  const duplicateVaccines = new Set<VaccineCode>();

  for (const vaccination of formVaccinations) {
    const { code, givenOn } = vaccination;
    const vaccineDetails = allVaccineCodesToDetailsMap[code];
    if (
      !vaccineDetails ||
      (PENTA_COMPONENT_VACCINES as readonly string[]).includes(code)
    )
      continue;

    for (const schedule of Object.values(vaccinationSchedule)) {
      schedule.doses.forEach((dose, doseIndex) => {
        if (!dose.codes.includes(code)) return;
        // this is a hacky way of getting round the fact that HepB acts as both its own scheduled vaccine and a part of Penta.
        if (code === "$IMM5" && dose.codes.length > 1) return;
        if (dose.completedDate) {
          duplicateVaccines.add(code);
        }
        if (dose.dueDate) {
          const oneWeekBeforeDue = subWeeks(dose.dueDate, 1);

          if (isBefore(givenOn ?? new Date(), oneWeekBeforeDue)) {
            earlyVaccinations.add(code);
          }
        }

        if (!vaccineDetails.previousVaccineCodes || doseIndex <= 0) return;

        const earlierDoses = schedule.doses.slice(0, doseIndex);
        const earlierIncompleteDoses = earlierDoses.filter(
          (d) => !d.completedDate,
        );
        const hasIncompleteDoses =
          earlierIncompleteDoses.length > 0 &&
          !formVaccinations.some((v) =>
            vaccineDetails.previousVaccineCodes?.includes(v.code),
          );

        if (hasIncompleteDoses) {
          for (const prevCode of vaccineDetails.previousVaccineCodes) {
            if (
              earlierIncompleteDoses.some((d) => d.codes.includes(prevCode))
            ) {
              missingVaccines.add(prevCode);
            }
          }
        }
      });
    }
  }

  return {
    missing: Array.from(missingVaccines),
    early: Array.from(earlyVaccinations),
    duplicate: Array.from(duplicateVaccines),
  };
};
